import { CloseCircleIcon, PlusCircleIcon, TrashIcon } from '@mm/icons';
import pencilAltIcon from 'assets/icons/pencil-alt.svg';
import { map } from 'lodash';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useLeadAssignmentGroupsCRUDActions } from '@mm/state/actions/LeadAssignmentsGroupCRUDActions';
import { LeadAssignmentGroup } from '@mm/state/interfaces/leadAssignmentGroups';
import CompanyUsersModel from '@mm/state/model/CompanyUsersModel';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import { AccordionItem } from '@mm/components';
import {
  ButtonColor,
  PrimaryButton,
} from '../../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import SelectorWithArrow from '../../../../../../../common/components/SelectorWithArrow/SelectorWithArrow';
import { colors } from '../../../../styles/colors';
import './AgentGroup.pcss';
import { ProfileImage } from '../../../../../liveChat/components/ProfileImage/ProfileImage';

type Props = {
  group: LeadAssignmentGroup;
  agents: CompanyUsersModel[];
};

export const AgentGroup = (props: Props) => {
  const { group, agents } = props;
  const [isAddAgentMode, setIsAddAgentMode] = useState(false);
  const [newNameValue, setNewNameValue] = useState(group.name || '');
  const [editName, setEditName] = useState(false);
  const activeChatbotId = useSelector(activeChatbotIdSelector);
  const [agentId, setAgentId] = useState<number>();
  const { update, delete: deleteGroup } = useLeadAssignmentGroupsCRUDActions();
  const [groupAgents, setGroupAgents] = useState<CompanyUsersModel[]>(
    group.agents ?? [],
  );

  const formattedAgents = agents.map(agent => {
    return {
      ...agent,
      name: `${agent.firstName} ${agent.lastName}`,
    };
  });

  const addAgentItems = formattedAgents.filter(
    agent => !map(groupAgents, 'id').includes(agent.id),
  );

  const getSelectedOption = () => {
    if (agentId) {
      return formattedAgents.find(o => o.id === agentId);
    }
  };

  const onAddAgent = () => {
    const selectedAgent = getSelectedOption();
    if (selectedAgent) {
      setAgentId(undefined);
      const newAgents = [selectedAgent, ...groupAgents];
      setGroupAgents(newAgents);
      setIsAddAgentMode(false);
      update(activeChatbotId, group.id, {
        agentIds: map(newAgents, 'id'),
      });
    }
  };

  const onRemoveAgent = (agentId: number) => {
    const filteredAgents = groupAgents.filter(agent => agent.id !== agentId);
    setGroupAgents(filteredAgents);
    update(activeChatbotId, group.id, {
      agentIds: map(filteredAgents, 'id'),
    });
  };

  const onDeleteGroup = () => {
    deleteGroup(activeChatbotId, group.id);
  };

  const groupNameInputComponent = editName ? (
    <p className="agent-group__header-name-item">
      <input
        className="agent-group__input-container"
        value={newNameValue}
        placeholder="Value"
        onChange={event => setNewNameValue(event.target.value)}
      />
      <PrimaryButton
        color={ButtonColor.blue}
        className="agent-group__input-btn"
        disabled={newNameValue.trim().length === 0}
        onClick={() => {
          update(activeChatbotId, group.id, {
            name: newNameValue,
          });
          setEditName(false);
        }}
      >
        Edit
      </PrimaryButton>
      <PrimaryButton
        color={ButtonColor.invertedRed}
        className="agent-group__input-btn"
        onClick={() => {
          setNewNameValue(group.name || '');
          setEditName(false);
        }}
      >
        Cancel
      </PrimaryButton>
    </p>
  ) : (
    <p className="agent-group__header-name-item">
      {newNameValue}
      <img
        src={pencilAltIcon}
        className="agent-group__edit-icon"
        alt=""
        onClick={() => setEditName(true)}
      />
    </p>
  );

  const addAgentComponent = isAddAgentMode ? (
    <SelectorWithArrow
      selectedItem={getSelectedOption()}
      onChange={id => setAgentId(Number(id))}
      items={addAgentItems}
      defaultLabel="Choose an Agent"
      buttonText="Add"
      buttonAction={onAddAgent}
      buttonClassName="lead-assignment__add-new-agent-button"
      className="agent-group__agent-selector"
    />
  ) : (
    <PrimaryButton
      className="lead-assignment__add-new-button"
      color={ButtonColor.secondaryBlue}
      onClick={() => setIsAddAgentMode(true)}
    >
      <PlusCircleIcon
        color={colors.accentBlue}
        className="lead-assignment__add-new-button-icon"
      />
      Add Agent
    </PrimaryButton>
  );
  return (
    <AccordionItem
      key={group.id}
      openOnMount={true}
      arrowPosition="left"
      closeOnOutsideClick={false}
      titleComponent={
        <div className="agent-group__header">
          {groupNameInputComponent}
          <div className="agent-group__delete-icon">
            <TrashIcon onClick={onDeleteGroup} />
          </div>
        </div>
      }
    >
      {groupAgents.map(agent => {
        return (
          <div className="agent-group__agent-item" key={agent.id}>
            <ProfileImage
              className="agent-group__agent-item-image"
              altText=""
              profilePictureUrl={agent.image}
            />
            <span>
              {agent.firstName} {agent.lastName}
            </span>
            {groupAgents.length !== 1 && (
              <CloseCircleIcon
                onClick={() => onRemoveAgent(agent.id)}
                className="agent-group__agent-remove-icon"
              />
            )}
          </div>
        );
      })}
      {!!addAgentItems.length && addAgentComponent}
    </AccordionItem>
  );
};
